import { useCallback, useEffect, useRef, useState } from 'react'
import type { PointerEvent as ReactPointerEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { EyeOff, Highlighter, Undo2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { drawAnnotations, rectFromPoints } from '@/lib/feedback/annotations'
import type { Annotation, AnnotationTool, Point } from '@/lib/feedback/annotations'
import { loadImage } from '@/lib/feedback/image'

// Smaller drags are stray clicks, not rectangles.
const MIN_SIZE = 4

interface ScreenshotEditorProps {
  src: string
  onSave: (dataUrl: string) => void
  onCancel: () => void
}

export function ScreenshotEditor({ src, onSave, onCancel }: ScreenshotEditorProps) {
  const { t } = useTranslation('feedback')
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const start = useRef<Point | null>(null)
  const [image, setImage] = useState<HTMLImageElement | null>(null)
  const [tool, setTool] = useState<AnnotationTool>('highlight')
  const [annotations, setAnnotations] = useState<Annotation[]>([])
  const [draft, setDraft] = useState<Annotation | null>(null)

  useEffect(() => {
    let cancelled = false
    loadImage(src)
      .then((loaded) => {
        if (!cancelled) setImage(loaded)
      })
      .catch(() => {
        if (!cancelled) setImage(null)
      })
    return () => {
      cancelled = true
    }
  }, [src])

  const paint = useCallback(
    (items: Annotation[]) => {
      const canvas = canvasRef.current
      const ctx = canvas?.getContext('2d')
      if (!canvas || !ctx || !image) return
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(image, 0, 0)
      drawAnnotations(ctx, items)
    },
    [image],
  )

  // Sizing a canvas clears it, so it happens once per image, before painting.
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !image) return
    canvas.width = image.naturalWidth
    canvas.height = image.naturalHeight
  }, [image])

  useEffect(() => {
    paint(draft ? [...annotations, draft] : annotations)
  }, [paint, annotations, draft])

  const toImagePoint = (event: ReactPointerEvent<HTMLCanvasElement>): Point => {
    const canvas = event.currentTarget
    const box = canvas.getBoundingClientRect()
    const clamp = (value: number, max: number) => Math.min(Math.max(value, 0), max)
    return {
      x: clamp(((event.clientX - box.left) * canvas.width) / box.width, canvas.width),
      y: clamp(((event.clientY - box.top) * canvas.height) / box.height, canvas.height),
    }
  }

  const handlePointerDown = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!image) return
    event.currentTarget.setPointerCapture?.(event.pointerId)
    start.current = toImagePoint(event)
  }

  const handlePointerMove = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!start.current) return
    setDraft({ tool, ...rectFromPoints(start.current, toImagePoint(event)) })
  }

  const handlePointerUp = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!start.current) return
    const rect = rectFromPoints(start.current, toImagePoint(event))
    start.current = null
    setDraft(null)
    if (rect.width >= MIN_SIZE && rect.height >= MIN_SIZE) {
      setAnnotations((current) => [...current, { tool, ...rect }])
    }
  }

  // The browser took the pointer back (a scroll, a system gesture): drop the
  // half-drawn rectangle rather than keep one the user never finished.
  const handlePointerCancel = () => {
    start.current = null
    setDraft(null)
  }

  const handleSave = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    paint(annotations)
    onSave(canvas.toDataURL('image/png'))
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant={tool === 'highlight' ? 'default' : 'outline'}
          aria-pressed={tool === 'highlight'}
          onClick={() => setTool('highlight')}
        >
          <Highlighter className="mr-1 h-4 w-4" />
          {t('editor.highlight')}
        </Button>
        <Button
          type="button"
          size="sm"
          variant={tool === 'redact' ? 'default' : 'outline'}
          aria-pressed={tool === 'redact'}
          onClick={() => setTool('redact')}
        >
          <EyeOff className="mr-1 h-4 w-4" />
          {t('editor.redact')}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={annotations.length === 0}
          onClick={() => setAnnotations((current) => current.slice(0, -1))}
        >
          <Undo2 className="mr-1 h-4 w-4" />
          {t('editor.undo')}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">{t('editor.hint')}</p>
      <div className="max-h-[60vh] overflow-auto rounded-md border">
        <canvas
          ref={canvasRef}
          role="img"
          aria-label={t('editor.canvasLabel')}
          className="block h-auto max-w-full cursor-crosshair touch-none"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('editor.cancel')}
        </Button>
        <Button type="button" onClick={handleSave} disabled={!image}>
          {t('editor.save')}
        </Button>
      </div>
    </div>
  )
}
